import React, { useContext, useEffect, useState, useMemo } from "react";
import {
    Box, Button, MenuItem, Select, TextField, Stack, // Using Stack for layout
    FormControl, InputLabel, ListSubheader, InputAdornment, // Added for better forms/inputs
    Typography, // For conditional message
    useTheme, // Add useTheme hook
    // Alert // Optional: Alternative for conditional message
} from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { WalletsContext } from "../../state/context/wallets.context";
import { AuthContext } from "../../state/context/auth.context";
import { API_BASE_URL } from "../../utils/constants";

const QuickBuy = ({ tokenId }) => {
    const { t } = useTranslation();
    const theme = useTheme(); // Add theme hook
    // --- Context ---
    const { state: authState } = useContext(AuthContext);
    const { state: walletsState } = useContext(WalletsContext);
    const { isConnected } = authState.user || {};
    const walletList = walletsState.wallets || [];
    const walletGroups = walletsState.walletGroups || [];
    const { mainWallet } = walletsState;

    // --- State ---
    const [selectedWalletOrGroupId, setSelectedWalletOrGroupId] = useState("");
    const [amount, setAmount] = useState("");
    const [slippage, setSlippage] = useState("0"); // Keep as string for consistency with input/localStorage
    const [isBuying, setIsBuying] = useState(false); // State to disable button during API call


    // Filter favorite wallets using useMemo for slight optimization
    const favoriteWallets = useMemo(() => {
        let walletsList = walletList.filter((wallet) => wallet.isFavorite);
        if (mainWallet) {
            walletsList = [...walletsList, mainWallet];
        }
        return walletsList;

    }, [walletList, mainWallet]);

    // --- Effects ---
    // Load cached values from localStorage on component mount
    useEffect(() => {
        const cachedWallet = localStorage.getItem("quickBuy_selectedWallet");
        const cachedAmount = localStorage.getItem("quickBuy_amount");
        const cachedSlippage = localStorage.getItem("quickBuy_slippage");

        // Validate cached values before setting state
        if (cachedSlippage && !isNaN(Number(cachedSlippage))) setSlippage(cachedSlippage);
        // Check if the cached wallet/group ID still exists
        const isValidWallet = walletList.some(w => w._id === cachedWallet);
        const isValidGroup = walletGroups.some(g => g._id === cachedWallet);
        if (cachedWallet && (isValidWallet || isValidGroup) || (cachedWallet === "ODIN_MAIN_WALLET" && mainWallet)) {
            setSelectedWalletOrGroupId(cachedWallet);
        } else if (favoriteWallets.length > 0) {
            // Default to first favorite if cached is invalid or none
            setSelectedWalletOrGroupId(favoriteWallets[0]._id);
            localStorage.setItem("quickBuy_selectedWallet", favoriteWallets[0]._id);
        } else if (walletGroups.length > 0) {
            // Default to first group if no favorites
            setSelectedWalletOrGroupId(walletGroups[0]._id);
            localStorage.setItem("quickBuy_selectedWallet", walletGroups[0]._id);
        }

        if (cachedAmount) setAmount(cachedAmount);

    }, [walletList, walletGroups, favoriteWallets, mainWallet]); // Re-run if wallets load after initial render

    // --- Handlers ---
    const handleWalletChange = (e) => {
        const newValue = e.target.value;
        setSelectedWalletOrGroupId(newValue);
        localStorage.setItem("quickBuy_selectedWallet", newValue);
    };

    const handleAmountChange = (e) => {
        const newValue = e.target.value;
        // Allow only numbers (and potentially a decimal point)
        if (/^\d*\.?\d*$/.test(newValue)) {
            setAmount(newValue);
            localStorage.setItem("quickBuy_amount", newValue);
        }
    };

    const handleSlippageChange = (e) => {
        const newValue = e.target.value;
        // Allow only numbers (and potentially a decimal point) - adjust validation as needed
        if (/^\d*\.?\d*$/.test(newValue)) {
            setSlippage(newValue);
            localStorage.setItem("quickBuy_slippage", newValue);
        }
    };

    const handleQuickBuy = async () => {
        // Basic validation
        const numericAmount = amount * 1e11;
        if (!selectedWalletOrGroupId || isNaN(numericAmount) || numericAmount <= 0) {
            toast.error("Please select a wallet/group and enter a valid amount.");
            return;
        }
        const numericSlippage = parseFloat(slippage);
        if (isNaN(numericSlippage) || numericSlippage < 0 || numericSlippage > 100) {
            toast.error("Please enter a valid slippage percentage (0-100).");
            return;
        }


        setIsBuying(true); // Disable button
        let targetWallets = [];

        // Check if it's an individual wallet
        const selectedWalletObj = walletList.find((wallet) => wallet._id === selectedWalletOrGroupId);
        if (selectedWalletObj) {
            targetWallets.push(selectedWalletObj);
        } else {
            // Otherwise, assume it's a group and get all wallets in that group
            targetWallets = walletList.filter((wallet) => wallet.group === selectedWalletOrGroupId);
            if (mainWallet && selectedWalletOrGroupId === "ODIN_MAIN_WALLET") {
                targetWallets.push(mainWallet);
            }
            if (targetWallets.length === 0) {
                // This case shouldn't happen if the select list is correct, but good to check
                toast.error("Selected group contains no wallets.");
                setIsBuying(false);
                return;
            }
        }

        // Process buy for each wallet in the selection (could be 1 or many if group)
        // Consider if processing should be parallel or sequential
        for (const wallet of targetWallets) {
            const toastId = toast.loading(`Buying ${ numericAmount } ${ tokenId } with ${ wallet.name }...`, { /* ... toast options ... */ });

            try {
                // Fetch current price (consider caching or getting price once if buying with group)
                const priceResp = await fetch(`${ API_BASE_URL }/token/${ tokenId }/price`);
                if (!priceResp.ok) throw new Error('Failed to fetch token price');
                const priceData = await priceResp.json();
                const currentPrice = priceData.price; // Assuming the API returns { price: number }

                let walletData = wallet.serializeIdentity; // Assuming this exists and works
                let chossenWallet = {};
                if (walletData) {
                    chossenWallet = {
                        walletData: walletData,
                        type: "BTC"
                    };
                } else {
                    chossenWallet = {
                        walletData: wallet.privateKey,
                        type: "ICP"
                    };
                }

                // Construct payload
                const payload = {
                    ...chossenWallet,
                    tokenId,
                    amount: numericAmount, // Send numeric amount
                    // Slippage format based on API requirements
                    settings: numericSlippage == 0 ? [{ slippage: [] }] : [{ slippage: [[currentPrice, Math.floor(numericSlippage * 1e5)]] }], // Slippage needs scaling? e.g. 1% -> 100 ? Confirm API spec.
                };

                const response = await fetch(`${ API_BASE_URL }/quickbuy`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });

                const data = await response.json();

                if (!response.ok) {
                    toast.update(toastId, { render: `Buy failed for ${ wallet.name }: ${ data.error || 'Unknown error' }`, type: "error", isLoading: false, autoClose: 5000 });
                } else {
                    toast.update(toastId, { render: `Bought ${ numericAmount } ${ tokenId } with ${ wallet.name }!`, type: "success", isLoading: false, autoClose: 5000 });
                }
            } catch (error) {
                console.error(`QuickBuy error for wallet ${ wallet.name }:`, error);
                toast.update(toastId, { render: `Buy error for ${ wallet.name }: ${ error.message }`, type: "error", isLoading: false, autoClose: 5000 });
            }
        }
        setIsBuying(false); // Re-enable button
    };

    // --- Derived State for Validation ---
    const isValidAmount = useMemo(() => {
        const num = parseFloat(amount);
        return !isNaN(num) && num > 0;
    }, [amount]);

    const isValidSlippage = useMemo(() => {
        const num = parseFloat(slippage);
        return !isNaN(num) && num >= 0 && num <= 100;
    }, [slippage]);

    const canBuy = isConnected && selectedWalletOrGroupId && isValidAmount && isValidSlippage && !isBuying;

    // --- Conditional Render: Not Connected or No Wallets ---
    // Show message if not connected OR if there are no wallets/groups to select from
    const noWalletsAvailable = favoriteWallets.length === 0 && walletGroups.length === 0;
    if (!isConnected || noWalletsAvailable) {
        return (
            <Box sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${ theme.palette.divider }`,
                borderRadius: theme.shape.borderRadius,
            }}>
                <Typography variant="body2" color="text.secondary">
                    {!isConnected
                        ? t('common.connectWalletToUse')
                        : t('common.createWalletFirst')
                    }
                </Typography>
                {/* Optional: Use Alert for more emphasis */}
                {/* <Alert severity="info" sx={{ justifyContent: 'center' }}>
                     { !isConnected ? "Connect wallet to use Quick Buy." : "Create a wallet or group first."}
                 </Alert> */}
            </Box>
        );
    }

    // --- Main Render: Quick Buy Controls ---
    return (
        // Use Stack for layout, center items vertically, control spacing
        <Stack
            direction="row"
            spacing={1.5} // Adjust spacing as needed
            alignItems="center"
            justifyContent="center" // Align items to the start
            sx={{
                width: '100%',
                px: 1,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${ theme.palette.divider }`,
                borderRadius: theme.shape.borderRadius,
                py: 1.5,
            }} // Add padding if needed within its container
        >
            {/* Wallet/Group Selector */}
            <FormControl size="small" sx={{ minWidth: 160, flexShrink: 0 }}> {/* Prevent shrinking too much */}
                <InputLabel id="quickbuy-wallet-label">{t('common.walletGroup')}</InputLabel>
                <Select
                    labelId="quickbuy-wallet-label"
                    label={t('common.walletGroup')}
                    value={selectedWalletOrGroupId}
                    onChange={handleWalletChange}
                    displayEmpty // Important for placeholder logic
                >
                    {/* Placeholder Option */}
                    <MenuItem value="" disabled>
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            {t('common.selectWalletGroup')}
                        </Typography>
                    </MenuItem>

                    {/* Favorite Wallets Group */}
                    {favoriteWallets.length > 0 && (
                        <ListSubheader sx={{ color: theme.palette.text.primary }}>
                            {t('common.favorites')}
                        </ListSubheader>
                    )}
                    {favoriteWallets.map((wallet) => (
                        <MenuItem key={wallet._id} value={wallet._id}>
                            <Typography color="text.primary">{wallet.name}</Typography>
                        </MenuItem>
                    ))}

                    {/* Wallet Groups Group */}
                    {walletGroups.length > 0 && (
                        <ListSubheader sx={{ color: theme.palette.text.primary }}>
                            {t('common.groups')}
                        </ListSubheader>
                    )}
                    {walletGroups.map((group) => (
                        <MenuItem key={group._id} value={group._id}>
                            <Typography color="text.primary">{group.name} (Group)</Typography>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Amount Input */}
            <TextField
                label={t('common.quickBuyPlaceholder')}
                size="small"
                variant="outlined"
                value={amount}
                onChange={handleAmountChange}
                sx={{ width: '100px' }} // Control width
                // Add type="text" and inputMode="decimal" for better mobile experience
                type="text"
                inputMode="decimal"
            />

            {/* Slippage Input */}
            <TextField
                label={t('common.slippage')}
                size="small"
                variant="outlined"
                value={slippage}
                onChange={handleSlippageChange}
                type="mumber" // Use text + validation + inputMode for better control
                inputMode="decimal"
                InputProps={{
                    // Use InputAdornment for proper styling/alignment
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{
                    maxLength: 5,
                    // Optional: Add min/max validation if needed
                    min: 0,
                    max: 100,
                }} // Limit input length reasonably
                sx={{ width: '90px' }} // Slightly smaller width maybe
            />

            {/* Quick Buy Button */}
            <Button
                variant="contained"
                size="small"
                color="secondary" // Use primary color for main action
                onClick={handleQuickBuy}
                disabled={!canBuy} // Disable based on validation and loading state
                sx={{
                    whiteSpace: "nowrap", // Prevent text wrapping
                    // Add margin left if needed to push away from inputs, or rely on Stack spacing
                    // ml: 'auto', // Push button to the right if desired

                    //disable style
                    opacity: canBuy ? 1 : 0.5,
                    cursor: canBuy ? "pointer" : "not-allowed",
                    // Use theme colors for disabled state
                    '&.Mui-disabled': {
                        backgroundColor: theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                    },
                }}
            >
                {isBuying ? t('common.buying') : t('common.quickBuy')}
            </Button>
        </Stack>
    );
};

export default QuickBuy;